/**
 * One-shot migration: localStorage mock workspace → live `/api/workspace`.
 * Runs only when the cloud doc is empty and this browser still has local data.
 * Uses atomic `bootstrapWorkspace` so a mid-flight failure does not orphan data
 * behind a permanent "migrated" flag.
 */

import type { DailyGroup, LlmWikiPage, NoteItem, ReminderItem } from '../../domain/types';
import type { IWorkspaceClient } from './types';
import { StorageService } from '../storageService';

const MIGRATION_FLAG = 'zunelo_workspace_migrated_v1';

function hasLocalLines(groups: DailyGroup[]): boolean {
  return groups.some((g) => g.lines.length > 0);
}

function cloudLooksEmpty(cloud: {
  reminders: ReminderItem[];
  notes: NoteItem[];
  dailyGroups: DailyGroup[];
  dailyDraft: string | null;
  dailySent: boolean;
  llmWiki: LlmWikiPage[] | null;
}): boolean {
  if (cloud.llmWiki === null) return false;
  return (
    cloud.reminders.length === 0 &&
    cloud.notes.length === 0 &&
    !hasLocalLines(cloud.dailyGroups) &&
    !cloud.dailyDraft?.trim() &&
    !cloud.dailySent &&
    cloud.llmWiki.length === 0
  );
}

/**
 * If the authenticated cloud workspace is empty, push legacy localStorage
 * reminders / notes / daily / wiki once, then set a flag so we never overwrite
 * again. A non-empty cloud wiki also blocks migration so a stale device can
 * never wipe wiki pages already stored in Firestore.
 *
 * `llmWiki: null` means the cloud wiki state is UNKNOWN (read failed): skip
 * migration entirely and do NOT set the flag so it retries once the read
 * succeeds — never migrate blind, or a failed wiki read could allow a stale
 * device to overwrite (wipe) a non-empty cloud wiki.
 */
export async function migrateLocalWorkspaceToolsIfNeeded(
  client: IWorkspaceClient,
  cloud: {
    reminders: ReminderItem[];
    notes: NoteItem[];
    dailyGroups: DailyGroup[];
    dailyDraft: string | null;
    dailySent: boolean;
    llmWiki: LlmWikiPage[] | null;
  },
): Promise<{
  reminders: ReminderItem[];
  notes: NoteItem[];
  dailyGroups: DailyGroup[];
  dailyDraft: string | null;
  llmWiki: LlmWikiPage[];
} | null> {
  if (typeof localStorage === 'undefined') return null;
  if (localStorage.getItem(MIGRATION_FLAG) === '1') return null;
  if (cloud.llmWiki === null) {
    // Unknown cloud wiki state: never migrate blind.
    return null;
  }
  if (!cloudLooksEmpty(cloud)) {
    localStorage.setItem(MIGRATION_FLAG, '1');
    return null;
  }

  const localReminders = StorageService.get<ReminderItem[]>('reminders', []);
  const localNotes = StorageService.get<NoteItem[]>('notes', []);
  const localGroups = StorageService.get<DailyGroup[]>('dailyGroups', []);
  const localDraft = StorageService.get<string | null>('dailyDraft', null);
  const localWiki = StorageService.get<LlmWikiPage[]>('llmWiki', []);

  const hasLocal =
    localReminders.length > 0 ||
    localNotes.length > 0 ||
    hasLocalLines(localGroups) ||
    Boolean(localDraft?.trim()) ||
    localWiki.length > 0;

  if (!hasLocal) {
    localStorage.setItem(MIGRATION_FLAG, '1');
    return null;
  }

  const dailyGroups = localGroups.length ? localGroups : cloud.dailyGroups;

  await client.bootstrapWorkspace({
    reminders: localReminders,
    notes: localNotes,
    dailyGroups,
    dailyDraft: localDraft,
    dailySentAt: null,
    llmWiki: localWiki,
  });

  localStorage.setItem(MIGRATION_FLAG, '1');

  return {
    reminders: localReminders,
    notes: localNotes,
    dailyGroups,
    dailyDraft: localDraft,
    llmWiki: localWiki,
  };
}
